'use client'

import { Dispatch, SetStateAction, useRef, useEffect, useCallback } from 'react'

import { ISearchResponse } from '@/_types/search'

import ADesktopSearchResults from './a-desktop-search-results'

interface ADesktopSearchProps {
	searchOpen: boolean
	setSearchOpen: Dispatch<SetStateAction<boolean>>
	searchQuery: string
	onSearchQueryChange: (query: string) => void
	onSearchClose: () => void
	searchData: ISearchResponse | undefined
	isSearchLoading: boolean
	isSearching: boolean
	hasSearchTerm: boolean
	onLinkClick: () => void
}

const ADesktopSearch = ({
	searchOpen,
	setSearchOpen,
	searchQuery,
	onSearchQueryChange,
	onSearchClose,
	searchData,
	isSearchLoading,
	isSearching,
	hasSearchTerm,
	onLinkClick
}: ADesktopSearchProps) => {
	const searchInputRef = useRef<HTMLInputElement>(null)
	const searchContainerRef = useRef<HTMLDivElement>(null)
	const searchButtonRef = useRef<HTMLButtonElement>(null)
	const searchResultsRef = useRef<HTMLDivElement>(null)
	const isTogglingViaButtonRef = useRef(false)

	// Auto-focus input when search opens
	useEffect(() => {
		if (searchOpen && searchInputRef.current) {
			// Small delay to ensure animation starts before focus
			const timer = setTimeout(() => {
				searchInputRef.current?.focus()
			}, 100)
			return () => clearTimeout(timer)
		}
	}, [searchOpen])

	// Close search on Escape key
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && searchOpen) {
				onSearchClose()
			}
		}

		document.addEventListener('keydown', handleEscape)
		return () => document.removeEventListener('keydown', handleEscape)
	}, [searchOpen, onSearchClose])

	const handleSearchToggle = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			e.stopPropagation()

			// Mark that we're toggling via button
			isTogglingViaButtonRef.current = true

			if (searchOpen) {
				onSearchClose()
			} else {
				setSearchOpen(true)
			}

			// Reset flag after a delay
			setTimeout(() => {
				isTogglingViaButtonRef.current = false
			}, 100)
		},
		[searchOpen, setSearchOpen, onSearchClose]
	)

	const handleSearchInputBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			// If we're toggling via button, don't close here (button will handle it)
			if (isTogglingViaButtonRef.current) {
				return
			}

			// Check if focus is moving to the search button
			const relatedTarget = e.relatedTarget as HTMLElement

			if (searchButtonRef.current?.contains(relatedTarget)) {
				return
			}

			// Check if clicking in results area
			if (searchResultsRef.current?.contains(relatedTarget)) {
				return
			}

			// Delay close to allow result clicks to complete
			setTimeout(() => {
				// Double-check we're not toggling
				if (!isTogglingViaButtonRef.current) {
					onSearchClose()
				}
			}, 150)
		},
		[onSearchClose]
	)

	const handleSearchSubmit = useCallback((e: React.FormEvent) => {
		e.preventDefault()
		// Form submission is handled by the search results links
	}, [])

	const handleSearchInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onSearchQueryChange(e.target.value)
		},
		[onSearchQueryChange]
	)

	const handleResultClick = useCallback(() => {
		onSearchClose()
		onLinkClick()
	}, [onSearchClose, onLinkClick])

	return (
		<>
			<div className='desktop-header__search-container' ref={searchContainerRef}>
				<form
					className={`desktop-header__search-input-wrapper ${
						searchOpen ? 'desktop-header__search-input-wrapper--open' : ''
					}`}
					onSubmit={handleSearchSubmit}
				>
					<input
						ref={searchInputRef}
						type='text'
						placeholder='Hľadať...'
						className='desktop-header__search-input'
						aria-label='Vyhľadávanie'
						onChange={handleSearchInputChange}
						onBlur={handleSearchInputBlur}
						value={searchQuery}
					/>
				</form>

				{searchOpen && (
					<ADesktopSearchResults
						ref={searchResultsRef}
						results={searchData?.data || []}
						isLoading={isSearchLoading}
						isSearching={isSearching}
						hasSearchTerm={hasSearchTerm}
						onResultClick={handleResultClick}
						searchQuery={searchQuery}
					/>
				)}
			</div>

			<button
				ref={searchButtonRef}
				type='button'
				className={`desktop-header__search-button ${
					searchOpen ? 'desktop-header__search-button--active' : ''
				}`}
				onMouseDown={handleSearchToggle}
				onClick={e => {
					e.preventDefault()
				}}
				aria-label={searchOpen ? 'Zatvoriť vyhľadávanie' : 'Otvoriť vyhľadávanie'}
				aria-expanded={searchOpen}
			>
				<svg className='sicofill desktop-header__search-button-icon'>
					<use href='/assets/layout/icons.svg#s_mag' />
				</svg>
			</button>
		</>
	)
}

export default ADesktopSearch
